import { Card, Circle, Heading, HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react'
import { usePitchDeckUpload } from '@src/components/sco/PitchDeckAuditor/hooks/usePitchDeckUpload'
import { UploadDocumentButton } from '@src/components/sco/UploadDocumentButton'
import { scoBackendUrl } from '@src/lib/startups-url.ts'
import { useState } from 'react'
import { LuTriangleAlert, LuUpload } from 'react-icons/lu'

/**
 * Empty-shell pitch decks (e.g. owned by a GroupRequest via getOrCreate) have no PDF yet.
 * Prompt for an upload without showing processing/auditor chrome.
 */
export function PitchDeckNeedsUpload({
  pitchDeckId,
  title,
  onUploaded,
}: {
  pitchDeckId: string
  title?: string | null
  onUploaded: () => void
}) {
  const { replaceDeckFile, replacing } = usePitchDeckUpload()
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  const busy = replacing || working

  const handleUploaded = async (_url: string, storedFileId?: string) => {
    setError(null)
    if (!storedFileId) {
      setError('Upload succeeded but no file id was returned. Please try again.')
      return
    }
    setWorking(true)
    try {
      const id = await replaceDeckFile(pitchDeckId, storedFileId, { analyze: true })
      if (id) {
        onUploaded()
      } else {
        setError('We could not start the audit. Please try again.')
        setWorking(false)
      }
    } catch (e: any) {
      setError(e?.message || 'Something went wrong starting the audit.')
      setWorking(false)
    }
  }

  return (
    <VStack gap={4} marginX={'auto'} maxWidth={'container.md'} paddingY={{ base: 4, md: 8 }} width={'full'}>
      <VStack gap={3} pt={{ base: 2, md: 8 }} textAlign={'center'}>
        <Text color={'fg.subtle'} letterSpacing={'widest'} textStyle={'labelXs'}>
          Startups.com Pitch Deck Auditor
        </Text>
        <Heading size={{ base: '2xl', md: '3xl' }}>Upload your pitch deck</Heading>
        <Text color={'fg.muted'} maxWidth={'container.sm'}>
          {title ? (
            <>
              <Text as={'span'} color={'fg'} fontWeight={'medium'}>
                {title}
              </Text>{' '}
              is ready — add a PDF to run the audit.
            </>
          ) : (
            <>This deck doesn&rsquo;t have a file yet. Upload a PDF to start the audit.</>
          )}
        </Text>
      </VStack>

      <Card.Root
        _hover={{ borderColor: 'orange.solid' }}
        borderColor={'border.emphasized'}
        borderStyle={'dashed'}
        borderWidth={'2px'}
        transition={'all 0.15s ease'}
        width={'full'}
      >
        <Card.Body py={10}>
          <VStack gap={2}>
            <Circle bg={'orange.subtle'} size={14}>
              <Icon as={busy ? Spinner : LuUpload} boxSize={6} color={'orange.fg'} />
            </Circle>
            <Heading size={'md'}>{busy ? 'Starting your audit…' : 'Upload your deck'}</Heading>
            <Text color={'fg.muted'} fontSize={'sm'} textAlign={'center'}>
              PDF up to 40&nbsp;MB. (Export PowerPoint / Keynote to PDF first.)
            </Text>
            <UploadDocumentButton
              allowedExtensions={['.pdf']}
              loading={busy}
              loadingText={'Starting…'}
              maxSizeInBytes={50 * 1024 * 1024}
              size={'lg'}
              storedFile={{ attachmentType: 'PitchDeck', contextKey: 'original' }}
              uploadUrl={scoBackendUrl('/assets/upload-pitch-deck')}
              variant={'cta'}
              onError={(e: any) => setError(e?.message || 'Upload failed. Please try again.')}
              onSuccess={handleUploaded}
            >
              <LuUpload />
              Choose a PDF
            </UploadDocumentButton>
            {error && (
              <HStack color={'red.fg'} fontSize={'sm'} gap={2}>
                <Icon as={LuTriangleAlert} boxSize={4} />
                <Text>{error}</Text>
              </HStack>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>
    </VStack>
  )
}
